import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const QUESTIONS = [
  { n: "01", text: "Have questions about our solutions?" },
  { n: "02", text: "Need pricing?" },
  { n: "03", text: "Want implementation advice?" },
  { n: "04", text: "Looking for partnership opportunities?" },
];

const CALL_TIMES = ["Morning", "Afternoon", "Evening"];

const INITIAL = {
  name: "",
  company: "",
  phone: "",
  email: "",
  time: "",
};

function Callback() {
  const reduce = useReducedMotion();
  const [form, setForm] = useState(INITIAL);
  const [sent, setSent] = useState(false);
  const [timeError, setTimeError] = useState(false);
  const fade = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (sent) setSent(false);
  };

  const onTime = (slot) => {
    setForm((current) => ({ ...current, time: slot }));
    setTimeError(false);
    if (sent) setSent(false);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (!form.time) {
      setTimeError(true);
      return;
    }
    setTimeError(false);
    setSent(true);
  };

  return (
    <section
      id="callback"
      className="relative overflow-hidden bg-white text-navy"
      aria-labelledby="callback-heading"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_100%_0%,rgba(229,184,11,0.12),transparent_55%),radial-gradient(50%_40%_at_0%_100%,rgba(12,31,27,0.07),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.38]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(12,31,27,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(12,31,27,0.07) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="absolute left-[6%] top-[14%] h-24 w-24 border border-navy/10" />
        <div className="absolute right-[8%] bottom-[16%] h-16 w-16 border border-gold/40" />
      </div>

      <div className="relative mx-auto max-w-8xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-14">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Speak with a specialist
            </p>
            <h2
              id="callback-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-royal [font-size:clamp(2rem,4.6vw,3.5rem)]"
            >
              Want one of our specialists
              <span className="mt-1 block">to contact you?</span>
            </h2>
            <p className="mt-5 max-w-lg border-l-2 border-gold pl-5 text-base leading-relaxed text-navy/60 sm:text-lg">
              Our team is ready to help.
            </p>

            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {QUESTIONS.map((item, i) => (
                <motion.li
                  key={item.n}
                  className="border border-navy/10 bg-[#f3f7f5] px-4 py-4"
                  initial={reduce ? false : { opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={
                    reduce ? { duration: 0 } : { duration: 0.4, delay: 0.05 * i, ease: EASE }
                  }
                >
                  <span className="font-mono text-[11px] font-semibold tracking-[0.18em] text-gold">
                    {item.n}
                  </span>
                  <p className="mt-2 text-[15px] font-semibold leading-snug tracking-tight text-navy">
                    {item.text}
                  </p>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.form
            className="relative overflow-hidden border border-navy/10 bg-white"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
            onSubmit={onSubmit}
            noValidate
          >
            <div className="h-[3px] bg-gradient-to-r from-gold via-royal to-gold" />
            <div className="px-6 py-8 sm:px-8 sm:py-9">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-navy/35">
                Request a call back
              </p>
              <p className="mt-2 text-2xl font-semibold tracking-tight text-royal">Leave your details</p>

              <div className="mt-7 grid gap-5 sm:grid-cols-2">
                <Field label="Name" name="name" value={form.name} onChange={onChange} required />
                <Field
                  label="Company"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  required
                />
                <Field
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  required
                  autoComplete="tel"
                />
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  autoComplete="email"
                />
                <fieldset className="sm:col-span-2">
                  <legend className="text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/40">
                    Best time to call
                    <span className="text-gold"> *</span>
                  </legend>
                  <div
                    className="mt-3 grid grid-cols-3 gap-2"
                    role="radiogroup"
                    aria-label="Best time to call"
                    aria-invalid={timeError || undefined}
                  >
                    {CALL_TIMES.map((slot) => {
                      const active = form.time === slot;
                      return (
                        <button
                          key={slot}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => onTime(slot)}
                          className={`min-h-12 border text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal ${
                            active
                              ? "border-navy bg-navy text-white"
                              : "border-navy/15 bg-[#f3f7f5] text-navy hover:border-navy/30"
                          }`}
                        >
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                  {timeError ? (
                    <p className="mt-2 text-sm text-royal" role="alert">
                      Select a time so we can reach you.
                    </p>
                  ) : null}
                </fieldset>
              </div>

              <button
                type="submit"
                className="mt-8 inline-flex min-h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-gold px-6 text-sm font-semibold text-navy transition hover:bg-[#f0c52a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal focus-visible:ring-offset-2 sm:w-auto"
              >
                Request a Call Back
                <ArrowIcon />
              </button>
              {sent ? (
                <p className="mt-4 text-sm text-navy/55" role="status">
                  Thank you. A specialist will reach you at the time you selected.
                </p>
              ) : null}
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  autoComplete,
}) {
  return (
    <div>
      <label
        htmlFor={`callback-${name}`}
        className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/40"
      >
        {label}
        {required ? <span className="text-gold"> *</span> : null}
      </label>
      <input
        id={`callback-${name}`}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={
          autoComplete ||
          (name === "name" ? "name" : name === "company" ? "organization" : undefined)
        }
        className="mt-2 min-h-12 w-full border border-navy/15 bg-[#f3f7f5] px-4 text-sm text-navy outline-none transition placeholder:text-navy/30 focus:border-gold focus:ring-2 focus:ring-gold/40"
      />
    </div>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path
        d="M3 8H13M13 8L9 4M13 8L9 12"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default Callback;
